import type { Spell } from '@character-creator/types';

export const level5Spells: Spell[] = [
  {
    id: 'animate-objects',
    name: 'Animate Objects',
    level: 5,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "Objects come to life at your command. Choose up to ten nonmagical objects within range that are not being worn or carried. Medium targets count as two objects, Large targets count as four objects, Huge targets count as eight objects. You can't animate any object larger than Huge. Each target animates and becomes a creature under your control until the spell ends or until reduced to 0 hit points.",
    higherLevel: "If you cast this spell using a spell slot of 6th level or higher, you can animate two additional objects for each slot level above 5th.",
    classes: ['bard', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'antilife-shell',
    name: 'Antilife Shell',
    level: 5,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Self (10-foot radius)',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: true,
    description: "A shimmering barrier extends out from you in a 10-foot radius and moves with you, remaining centered on you and hedging out creatures other than undead and constructs. The barrier lasts for the duration. The barrier prevents an affected creature from passing or reaching through. An affected creature can cast spells or make attacks with ranged or reach weapons through the barrier.",
    classes: ['druid'],
    source: 'srd-5.1',
  },
  {
    id: 'awaken',
    name: 'Awaken',
    level: 5,
    school: 'transmutation',
    ritual: false,
    castingTime: '8 hours',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'an agate worth at least 1,000 gp, which the spell consumes', materialCost: 1000, materialConsumed: true },
    duration: 'Instantaneous',
    concentration: false,
    description: "After spending the casting time tracing magical pathways within a precious gemstone, you touch a Huge or smaller beast or plant. The target must have either no Intelligence score or an Intelligence of 3 or less. The target gains an Intelligence of 10. The target also gains the ability to speak one language you know. If the target is a plant, it gains the ability to move its limbs, roots, vines, creepers, and so forth, and it gains senses similar to a human's.",
    classes: ['bard', 'druid'],
    source: 'srd-5.1',
  },
  {
    id: 'cloudkill',
    name: 'Cloudkill',
    level: 5,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '10 minutes',
    concentration: true,
    description: "You create a 20-foot-radius sphere of poisonous, yellow-green fog centered on a point you choose within range. The fog spreads around corners. It lasts for the duration or until strong wind disperses the fog, ending the spell. Its area is heavily obscured. When a creature enters the spell's area for the first time on a turn or starts its turn there, that creature must make a Constitution saving throw. The creature takes 5d8 poison damage on a failed save, or half as much damage on a successful one.",
    higherLevel: "When you cast this spell using a spell slot of 6th level or higher, the damage increases by 1d8 for each slot level above 5th.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'commune',
    name: 'Commune',
    level: 5,
    school: 'divination',
    ritual: true,
    castingTime: '1 minute',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'incense and a vial of holy or unholy water' },
    duration: '1 minute',
    concentration: false,
    description: "You contact your deity or a divine proxy and ask up to three questions that can be answered with a yes or no. You must ask your questions before the spell ends. You receive a correct answer for each question. Divine beings aren't necessarily omniscient, so you might receive \"unclear\" as an answer if a question pertains to information that lies beyond the deity's knowledge.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'commune-with-nature',
    name: 'Commune with Nature',
    level: 5,
    school: 'divination',
    ritual: true,
    castingTime: '1 minute',
    range: 'Self',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You briefly become one with nature and gain knowledge of the surrounding territory. In the outdoors, the spell gives you knowledge of the land within 3 miles of you. In caves and other natural underground settings, the radius is limited to 300 feet. The spell doesn't function where nature has been replaced by construction, such as in dungeons and towns.",
    classes: ['druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'cone-of-cold',
    name: 'Cone of Cold',
    level: 5,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: 'Self (60-foot cone)',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a small crystal or glass cone' },
    duration: 'Instantaneous',
    concentration: false,
    description: "A blast of cold air erupts from your hands. Each creature in a 60-foot cone must make a Constitution saving throw. A creature takes 8d8 cold damage on a failed save, or half as much damage on a successful one. A creature killed by this spell becomes a frozen statue until it thaws.",
    higherLevel: "When you cast this spell using a spell slot of 6th level or higher, the damage increases by 1d8 for each slot level above 5th.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'conjure-elemental',
    name: 'Conjure Elemental',
    level: 5,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 minute',
    range: '90 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'burning incense for air, soft clay for earth, sulfur and phosphorus for fire, or water and sand for water' },
    duration: '1 hour',
    concentration: true,
    description: "You call forth an elemental servant. Choose an area of air, earth, fire, or water that fills a 10-foot cube within range. An elemental of challenge rating 5 or lower appropriate to the area you chose appears in an unoccupied space within 10 feet of it. The elemental disappears when it drops to 0 hit points or when the spell ends.",
    higherLevel: "When you cast this spell using a spell slot of 6th level or higher, the challenge rating increases by 1 for each slot level above 5th.",
    classes: ['druid', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'contact-other-plane',
    name: 'Contact Other Plane',
    level: 5,
    school: 'divination',
    ritual: true,
    castingTime: '1 minute',
    range: 'Self',
    components: { verbal: true, somatic: false, material: false },
    duration: '1 minute',
    concentration: false,
    description: "You mentally contact a demigod, the spirit of a long-dead sage, or some other mysterious entity from another plane. Contacting this extraplanar intelligence can strain or even break your mind. When you cast this spell, make a DC 15 Intelligence saving throw. On a failure, you take 6d6 psychic damage and are insane until you finish a long rest. On a successful save, you can ask the entity up to five questions.",
    classes: ['warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'contagion',
    name: 'Contagion',
    level: 5,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: false },
    duration: '7 days',
    concentration: false,
    description: "Your touch inflicts disease. Make a melee spell attack against a creature within your reach. On a hit, the target is poisoned. At the end of each of the poisoned target's turns, the target must make a Constitution saving throw. If the target succeeds on three of these saves, it is no longer poisoned, and the spell ends. If the target fails three of these saves, the target is no longer poisoned, but choose one of the diseases below. The target is subjected to the chosen disease for the spell's duration.",
    classes: ['cleric', 'druid'],
    source: 'srd-5.1',
  },
  {
    id: 'creation',
    name: 'Creation',
    level: 5,
    school: 'illusion',
    ritual: false,
    castingTime: '1 minute',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a tiny piece of matter of the same type of the item you plan to create' },
    duration: 'Special',
    concentration: false,
    description: "You pull wisps of shadow material from the Shadowfell to create a nonliving object of vegetable matter within range: soft goods, rope, wood, or something similar. You can also use this spell to create mineral objects such as stone, crystal, or metal. The object created must be no larger than a 5-foot cube, and the object must be of a form and material that you have seen before.",
    higherLevel: "When you cast this spell using a spell slot of 6th level or higher, the cube increases by 5 feet for each slot level above 5th.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'dispel-evil-and-good',
    name: 'Dispel Evil and Good',
    level: 5,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'holy water or powdered silver and iron' },
    duration: '1 minute',
    concentration: true,
    description: "Shimmering energy surrounds and protects you from fey, undead, and creatures originating from beyond the Material Plane. For the duration, celestials, elementals, fey, fiends, and undead have disadvantage on attack rolls against you. You can end the spell early by using either of the following special functions: Break Enchantment or Dismissal.",
    classes: ['cleric', 'paladin'],
    source: 'srd-5.1',
  },
  {
    id: 'dominate-person',
    name: 'Dominate Person',
    level: 5,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "You attempt to beguile a humanoid that you can see within range. It must succeed on a Wisdom saving throw or be charmed by you for the duration. If you or creatures that are friendly to you are fighting it, it has advantage on the saving throw. While the target is charmed, you have a telepathic link with it as long as the two of you are on the same plane of existence.",
    higherLevel: "When you cast this spell using a 6th-level spell slot, the duration is concentration, up to 10 minutes. When you use a 7th-level spell slot, the duration is concentration, up to 1 hour. When you use a spell slot of 8th level or higher, the duration is concentration, up to 8 hours.",
    classes: ['bard', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'dream',
    name: 'Dream',
    level: 5,
    school: 'illusion',
    ritual: false,
    castingTime: '1 minute',
    range: 'Special',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a handful of sand, a dab of ink, and a writing quill plucked from a sleeping bird' },
    duration: '8 hours',
    concentration: false,
    description: "This spell shapes a creature's dreams. Choose a creature known to you as the target of this spell. The target must be on the same plane of existence as you. Creatures that don't sleep, such as elves, can't be contacted by this spell. You, or a willing creature you touch, enters a trance state, acting as a messenger. While in the trance, the messenger is aware of his or her surroundings, but can't take actions or move.",
    classes: ['bard', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'flame-strike',
    name: 'Flame Strike',
    level: 5,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'pinch of sulfur' },
    duration: 'Instantaneous',
    concentration: false,
    description: "A vertical column of divine fire roars down from the heavens in a location you specify. Each creature in a 10-foot-radius, 40-foot-high cylinder centered on a point within range must make a Dexterity saving throw. A creature takes 4d6 fire damage and 4d6 radiant damage on a failed save, or half as much damage on a successful one.",
    higherLevel: "When you cast this spell using a spell slot of 6th level or higher, the fire damage or the radiant damage (your choice) increases by 1d6 for each slot level above 5th.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'geas',
    name: 'Geas',
    level: 5,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 minute',
    range: '60 feet',
    components: { verbal: true, somatic: false, material: false },
    duration: '30 days',
    concentration: false,
    description: "You place a magical command on a creature that you can see within range, forcing it to carry out some service or refrain from some action or course of activity as you decide. If the creature can understand you, it must succeed on a Wisdom saving throw or become charmed by you for the duration. While the creature is charmed by you, it takes 5d10 psychic damage each time it acts in a manner directly counter to your instructions.",
    higherLevel: "When you cast this spell using a spell slot of 7th or 8th level, the duration is 1 year. When you cast this spell using a spell slot of 9th level, the spell lasts until it is ended by one of the spells mentioned above.",
    classes: ['bard', 'cleric', 'druid', 'paladin', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'greater-restoration',
    name: 'Greater Restoration',
    level: 5,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'diamond dust worth at least 100 gp, which the spell consumes', materialCost: 100, materialConsumed: true },
    duration: 'Instantaneous',
    concentration: false,
    description: "You imbue a creature you touch with positive energy to undo a debilitating effect. You can reduce the target's exhaustion level by one, or end one of the following effects on the target: One effect that charmed or petrified the target, One curse, including the target's attunement to a cursed magic item, Any reduction to one of the target's ability scores, or One effect reducing the target's hit point maximum.",
    classes: ['bard', 'cleric', 'druid'],
    source: 'srd-5.1',
  },
  {
    id: 'hallow',
    name: 'Hallow',
    level: 5,
    school: 'evocation',
    ritual: false,
    castingTime: '24 hours',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'herbs, oils, and incense worth at least 1,000 gp, which the spell consumes', materialCost: 1000, materialConsumed: true },
    duration: 'Until dispelled',
    concentration: false,
    description: "You touch a point and infuse an area around it with holy (or unholy) power. The area can have a radius up to 60 feet, and the spell fails if the radius includes an area already under the effect of a hallow spell. The affected area is subject to several effects including preventing certain creature types from entering.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'hold-monster',
    name: 'Hold Monster',
    level: 5,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '90 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a small, straight piece of iron' },
    duration: '1 minute',
    concentration: true,
    description: "Choose a creature that you can see within range. The target must succeed on a Wisdom saving throw or be paralyzed for the duration. This spell has no effect on undead. At the end of each of its turns, the target can make another Wisdom saving throw. On a success, the spell ends on the target.",
    higherLevel: "When you cast this spell using a spell slot of 6th level or higher, you can target one additional creature for each slot level above 5th. The creatures must be within 30 feet of each other when you target them.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'insect-plague',
    name: 'Insect Plague',
    level: 5,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '300 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a few grains of sugar, some kernels of grain, and a smear of fat' },
    duration: '10 minutes',
    concentration: true,
    description: "Swarming, biting locusts fill a 20-foot-radius sphere centered on a point you choose within range. The sphere spreads around corners. The sphere remains for the duration, and its area is lightly obscured. The sphere's area is difficult terrain. When the area appears, each creature in it must make a Constitution saving throw. A creature takes 4d10 piercing damage on a failed save, or half as much damage on a successful one.",
    higherLevel: "When you cast this spell using a spell slot of 6th level or higher, the damage increases by 1d10 for each slot level above 5th.",
    classes: ['cleric', 'druid', 'sorcerer'],
    source: 'srd-5.1',
  },
  {
    id: 'legend-lore',
    name: 'Legend Lore',
    level: 5,
    school: 'divination',
    ritual: false,
    castingTime: '10 minutes',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'incense worth at least 250 gp, which the spell consumes, and four ivory strips worth at least 50 gp each', materialCost: 450, materialConsumed: true },
    duration: 'Instantaneous',
    concentration: false,
    description: "Name or describe a person, place, or object. The spell brings to your mind a brief summary of the significant lore about the thing you named. The lore might consist of current tales, forgotten stories, or even secret lore that has never been widely known. If the thing you named isn't of legendary importance, you gain no information.",
    classes: ['bard', 'cleric', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'mass-cure-wounds',
    name: 'Mass Cure Wounds',
    level: 5,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "A wave of healing energy washes out from a point of your choice within range. Choose up to six creatures in a 30-foot-radius sphere centered on that point. Each target regains hit points equal to 3d8 + your spellcasting ability modifier. This spell has no effect on undead or constructs.",
    higherLevel: "When you cast this spell using a spell slot of 6th level or higher, the healing increases by 1d8 for each slot level above 5th.",
    classes: ['bard', 'cleric', 'druid'],
    source: 'srd-5.1',
  },
  {
    id: 'mislead',
    name: 'Mislead',
    level: 5,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: false, somatic: true, material: false },
    duration: '1 hour',
    concentration: true,
    description: "You become invisible at the same time that an illusory double of you appears where you are standing. The double lasts for the duration, but the invisibility ends if you attack or cast a spell. You can use your action to move your illusory double up to twice your speed and make it gesture, speak, and behave in whatever way you choose.",
    classes: ['bard', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'modify-memory',
    name: 'Modify Memory',
    level: 5,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "You attempt to reshape another creature's memories. One creature that you can see must make a Wisdom saving throw. If you are fighting the creature, it has advantage on the saving throw. On a failed save, the target becomes charmed by you for the duration. The charmed target is incapacitated and unaware of its surroundings, though it can still hear you. If it takes any damage or is targeted by another spell, this spell ends.",
    higherLevel: "If you cast this spell using a spell slot of 6th level or higher, you can alter the target's memories of an event that took place up to 7 days ago (6th level), 30 days ago (7th level), 1 year ago (8th level), or any time in the creature's past (9th level).",
    classes: ['bard', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'passwall',
    name: 'Passwall',
    level: 5,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pinch of sesame seeds' },
    duration: '1 hour',
    concentration: false,
    description: "A passage appears at a point of your choice that you can see on a wooden, plaster, or stone surface (such as a wall, a ceiling, or a floor) within range, and lasts for the duration. You choose the opening's dimensions: up to 5 feet wide, 8 feet tall, and 20 feet deep. The passage creates no instability in a structure surrounding it. When the opening disappears, any creatures or objects still in the passage created by the spell are safely ejected to an unoccupied space nearest to the surface on which you cast the spell.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'planar-binding',
    name: 'Planar Binding',
    level: 5,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 hour',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a jewel worth at least 1,000 gp, which the spell consumes', materialCost: 1000, materialConsumed: true },
    duration: '24 hours',
    concentration: false,
    description: "With this spell, you attempt to bind a celestial, an elemental, a fey, or a fiend to your service. The creature must be within range for the entire casting of the spell. At the completion of the casting, the target must make a Charisma saving throw. On a failed save, it is bound to serve you for the duration. If the creature was summoned or created by another spell, that spell's duration is extended to match the duration of this spell.",
    higherLevel: "When you cast this spell using a spell slot of a higher level, the duration increases to 10 days with a 6th-level slot, to 30 days with a 7th-level slot, to 180 days with an 8th-level slot, and to a year and a day with a 9th-level spell slot.",
    classes: ['bard', 'cleric', 'druid', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'raise-dead',
    name: 'Raise Dead',
    level: 5,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 hour',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a diamond worth at least 500 gp, which the spell consumes', materialCost: 500, materialConsumed: true },
    duration: 'Instantaneous',
    concentration: false,
    description: "You return a dead creature you touch to life, provided that it has been dead no longer than 10 days. If the creature's soul is both willing and at liberty to rejoin the body, the creature returns to life with 1 hit point. This spell also neutralizes any poisons and cures nonmagical diseases that affected the creature at the time it died. This spell doesn't remove magical diseases, curses, or similar effects; if these aren't first removed prior to casting the spell, they take effect when the creature returns to life.",
    classes: ['bard', 'cleric', 'paladin'],
    source: 'srd-5.1',
  },
  {
    id: 'reincarnate',
    name: 'Reincarnate',
    level: 5,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 hour',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'rare oils and unguents worth at least 1,000 gp, which the spell consumes', materialCost: 1000, materialConsumed: true },
    duration: 'Instantaneous',
    concentration: false,
    description: "You touch a dead humanoid or a piece of a dead humanoid. Provided that the creature has been dead no longer than 10 days, the spell forms a new adult body for it and then calls the soul to enter that body. If the target's soul isn't free or willing to do so, the spell fails. The magic fashions a new body for the creature to inhabit, which likely causes the creature's race to change. The DM rolls a d100 and consults the reincarnation table to determine what form the creature takes when restored to life.",
    classes: ['druid'],
    source: 'srd-5.1',
  },
  {
    id: 'scrying',
    name: 'Scrying',
    level: 5,
    school: 'divination',
    ritual: false,
    castingTime: '10 minutes',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a focus worth at least 1,000 gp, such as a crystal ball, a silver mirror, or a font filled with holy water', materialCost: 1000 },
    duration: '10 minutes',
    concentration: true,
    description: "You can see and hear a particular creature you choose that is on the same plane of existence as you. The target must make a Wisdom saving throw, which is modified by how well you know the target and the sort of physical connection you have to it. If a target knows you're casting this spell, it can fail the saving throw voluntarily if it wants to be observed.",
    classes: ['bard', 'cleric', 'druid', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'seeming',
    name: 'Seeming',
    level: 5,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '8 hours',
    concentration: false,
    description: "This spell allows you to change the appearance of any number of creatures that you can see within range. You give each target you choose a new, illusory appearance. An unwilling target can make a Charisma saving throw, and if it succeeds, it is unaffected by this spell. The spell disguises physical appearance as well as clothing, armor, weapons, and equipment.",
    classes: ['bard', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'telekinesis',
    name: 'Telekinesis',
    level: 5,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '10 minutes',
    concentration: true,
    description: "You gain the ability to move or manipulate creatures or objects by thought. When you cast the spell, and as your action each round for the duration, you can exert your will on one creature or object that you can see within range, causing the appropriate effect. You can affect the same target round after round, or choose a new one at any time. If you switch targets, the prior target is no longer affected by the spell.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'teleportation-circle',
    name: 'Teleportation Circle',
    level: 5,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 minute',
    range: '10 feet',
    components: { verbal: true, somatic: false, material: true, materialDescription: 'rare chalks and inks infused with precious gems worth 50 gp, which the spell consumes', materialCost: 50, materialConsumed: true },
    duration: '1 round',
    concentration: false,
    description: "As you cast the spell, you draw a 10-foot-diameter circle on the ground inscribed with sigils that link your location to a permanent teleportation circle of your choice whose sigil sequence you know and that is on the same plane of existence as you. A shimmering portal opens within the circle you drew and remains open until the end of your next turn. Any creature that enters the portal instantly appears within 5 feet of the destination circle or in the nearest unoccupied space if that space is occupied.",
    classes: ['bard', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'tree-stride',
    name: 'Tree Stride',
    level: 5,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "You gain the ability to enter a tree and move from inside it to inside another tree of the same kind within 500 feet. Both trees must be living and at least the same size as you. You must use 5 feet of movement to enter a tree. You instantly know the location of all other trees of the same kind within 500 feet and, as part of the move used to enter the tree, can either pass into one of those trees or step out of the tree you're in.",
    classes: ['druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'wall-of-force',
    name: 'Wall of Force',
    level: 5,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pinch of powder made by crushing a clear gemstone' },
    duration: '10 minutes',
    concentration: true,
    description: "An invisible wall of force springs into existence at a point you choose within range. The wall appears in any orientation you choose, as a horizontal or vertical barrier or at an angle. It can be free floating or resting on a solid surface. You can form it into a hemispherical dome or a sphere with a radius of up to 10 feet, or you can shape a flat surface made up of ten 10-foot-by-10-foot panels. Each panel must be contiguous with another panel. In any form, the wall is 1/4 inch thick. It lasts for the duration. If the wall cuts through a creature's space when it appears, the creature is pushed to one side of the wall (your choice which side).",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'wall-of-stone',
    name: 'Wall of Stone',
    level: 5,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a small block of granite' },
    duration: '10 minutes',
    concentration: true,
    description: "A nonmagical wall of solid stone springs into existence at a point you choose within range. The wall is 6 inches thick and is composed of ten 10-foot-by-10-foot panels. Each panel must be contiguous with at least one other panel. Alternatively, you can create 10-foot-by-20-foot panels that are only 3 inches thick. If the wall cuts through a creature's space when it appears, the creature is pushed to one side of the wall (your choice). If a creature would be surrounded on all sides by the wall (or the wall and another solid surface), that creature can make a Dexterity saving throw. On a success, it can use its reaction to move up to its speed so that it is no longer enclosed by the wall.",
    classes: ['druid', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
];
